import { REQUESTS } from "./apiConfig";

const API_ROUTES = {
    v1: {
        uploadeDocs:             "forgery/analyse/analyse_docs_v3/",
        list_analyse_documents:  "forgery/analyse/list_documents/",
        docAnalysis:             "forgery/analyse/get_analysis_v3/?document_id={0}",
        analysisAction:          "forgery/analyse/update_analysis_status/?analytics_id={0}",
    },
    v3: {
        analyzeDoc:              "forgery/v3/analyze/",
        listDocuments:           "forgery/v3/documents/",
        documentDetail:          "forgery/v3/documents/{0}/",
        documentComments:        "forgery/v3/documents/{0}/comments/",
        documentStatus:         "forgery/v3/documents/{0}/status/",
        featureConfigList:       "forgery/v3/feature-config/",
        featureConfigDetail:     "forgery/v3/feature-config/{0}/",
        adminUsageReport:        "forgery/v3/admin/usage-report/",

        // ── Batch ──────────────────────────────────────────────────────────
        batchSubmit:             "forgery/v3/batch/submit/",
        batchResult:             "forgery/v3/batch/{0}/result/",
        batchList:               "forgery/v3/batch/",

        // ── Detection config ─────────────────────────────────────────────────
        detectionConfigList:    "forgery/v3/detection-config/",
        detectionConfigDetail:  "forgery/v3/detection-config/{0}/",
        detectionConfigDefs:    "forgery/v3/detection-config/definitions/",
    },
};

const token = () => localStorage.getItem("token");

// ─────────────────────────────────────────────────────────────────────────────
// Detection flags — localStorage helpers
// ─────────────────────────────────────────────────────────────────────────────
 
const LS_KEY = (docType = "INVOICE") => `insightai_detection_flags_${docType}`;
 
/**
 * Read the current detection flags from localStorage.
 * Returns null if nothing is stored (backend will use org defaults).
 *
 * @param {string} docType   "INVOICE" | "BILL"
 * @returns {object|null}    { feature_key: bool } or null
 */
export const getLocalDetectionFlags = (docType = "INVOICE") => {
    try {
        const raw = localStorage.getItem(LS_KEY(docType));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};
 
/**
 * Write detection flags to localStorage.
 *
 * @param {string} docType
 * @param {object} flags    { feature_key: bool }
 */
export const saveLocalDetectionFlags = (docType, flags) => {
    try {
        localStorage.setItem(LS_KEY(docType), JSON.stringify(flags));
    } catch {}
};


// ─────────────────────────────────────────────────────────────────────────────
// Version 3 API calls
// ─────────────────────────────────────────────────────────────────────────────
export const uploadDocsHandler = (payload, useSocket = true, docType = "INVOICE") => {
    // Inject current detection flags from localStorage into the FormData.
    // The backend accepts these as a JSON string under the key 'detection_flags'.
    const flags = getLocalDetectionFlags(docType);
    if (flags) {
        // Remove any existing detection_flags entry to avoid duplicates
        payload.delete("detection_flags");
        payload.append("detection_flags", JSON.stringify(flags));
    }
 
    const route = `${API_ROUTES.v3.analyzeDoc}?ws=${useSocket ? "true" : "false"}`;
    return REQUESTS.postFile(route, token(), payload);
};

export const getDocumentListHandler = (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const route = query
        ? `${API_ROUTES.v3.listDocuments}?${query}`
        : API_ROUTES.v3.listDocuments;
    return REQUESTS.get(route, token());
};
 
export const docAnalysisHandler = (documentId) => {
    return REQUESTS.get(
        API_ROUTES.v3.documentDetail.replace("{0}", documentId),
        token(),
    );
};

export const updateDocCommentHandler = (documentId, comment) => {
    return REQUESTS.patch(
        API_ROUTES.v3.documentComments.format(documentId),
        token(),
        { user_comments: comment },
    );
};

export const updateDocStatusHandler = (documentId, status) => {
    return REQUESTS.patch(
        API_ROUTES.v3.documentStatus.format(documentId),
        // `forgery/v3/documents/${documentId}/status/`,
        token(),
        { status },
    );
};

export const getFeatureConfigsHandler = () => {
    return REQUESTS.get(API_ROUTES.v3.featureConfigList, token());
};

export const createFeatureConfigHandler = (payload) => {
    return REQUESTS.post(API_ROUTES.v3.featureConfigList, token(), payload);
};

export const getFeatureConfigHandler = (configId) => {
    return REQUESTS.get(
        API_ROUTES.v3.featureConfigDetail.format(configId),
        token(),
    );
};

export const updateFeatureConfigHandler = (configId, payload) => {
    return REQUESTS.put(
        API_ROUTES.v3.featureConfigDetail.format(configId),
        token(),
        payload,
    );
};

export const patchFeatureConfigHandler = (configId, payload) => {
    return REQUESTS.patch(
        API_ROUTES.v3.featureConfigDetail.format(configId),
        token(),
        payload,
    );
};

export const getAdminUsageReportHandler = (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const route = query
        ? `${API_ROUTES.v3.adminUsageReport}?${query}`
        : API_ROUTES.v3.adminUsageReport;
    return REQUESTS.get(route, token());
};

// ─────────────────────────────────────────────────────────────────────────────
// Detection config
// ─────────────────────────────────────────────────────────────────────────────
export const getDetectionConfigListHandler = () =>
    REQUESTS.get(API_ROUTES.v3.detectionConfigList, token());
 
export const getDetectionConfigHandler = (docType = "INVOICE") =>
    REQUESTS.get(
        API_ROUTES.v3.detectionConfigDetail.replace("{0}", docType),
        token(),
    ).then(res => {
        if (res?.data?.features) {
            saveLocalDetectionFlags(docType, res.data.features);
        }
        return res;
    });
 
export const saveDetectionConfigHandler = (docType = "INVOICE", flags = {}) => {
    // Optimistically write to localStorage so the next upload is instant
    saveLocalDetectionFlags(docType, flags);
    return REQUESTS.patch(
        API_ROUTES.v3.detectionConfigDetail.replace("{0}", docType),
        token(),
        { features: flags },
    );
};

export const replaceDetectionConfigHandler = (docType = "INVOICE", flags = {}) => {
    saveLocalDetectionFlags(docType, flags);
    return REQUESTS.post(
        API_ROUTES.v3.detectionConfigDetail.replace("{0}", docType),
        token(),
        { features: flags },
    );
};
 
export const getDetectionFeatureDefsHandler = () =>
    REQUESTS.get(API_ROUTES.v3.detectionConfigDefs, token());


// ─────────────────────────────────────────────────────────────────────────────
// Version 3 — Batch API
// ─────────────────────────────────────────────────────────────────────────────
export const submitBatchHandler = (payload, isFileUpload = true, docType = "INVOICE") => {
    const flags = getLocalDetectionFlags(docType);
 
    if (isFileUpload) {
        if (flags) {
            payload.delete("detection_flags");
            payload.append("detection_flags", JSON.stringify(flags));
        }
        return REQUESTS.postFile(API_ROUTES.v3.batchSubmit, token(), payload);
    }
 
    // Directory URL — JSON body
    if (flags) {
        payload.detection_flags = flags;
    }
    return REQUESTS.post(API_ROUTES.v3.batchSubmit, token(), payload);
};
 
export const getBatchResultHandler = (refId) =>
    REQUESTS.get(
        API_ROUTES.v3.batchResult.replace("{0}", refId),
        token(),
    );
 
export const getBatchListHandler = (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const route = query
        ? `${API_ROUTES.v3.batchList}?${query}`
        : API_ROUTES.v3.batchList;
    return REQUESTS.get(route, token());
};

// ─────────────────────────────────────────────────────────────────────────────
// Version 1 API calls
// ─────────────────────────────────────────────────────────────────────────────

export const listAnalyseDocumentsHandler = () => {
    return REQUESTS.get(API_ROUTES.v1.list_analyse_documents, token());
};

export const docAnalysisHandlerV1 = (documentId) => {
    return REQUESTS.get(
        API_ROUTES.v1.docAnalysis.format(documentId),
        token(),
    );
};

export const analysisActionHandler = (analyticsId, payload) => {
    return REQUESTS.put(
        API_ROUTES.v1.analysisAction.format(analyticsId),
        token(),
        payload,
    );
};